import React from "react";
import { motion } from "framer-motion";
import { Calendar, MessageCircle, Award, Video, HeartHandshake, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { CLINIC, HERO_POSTER, TRUST_BADGES } from "../lib/clinic";

const ICONS = { Award, Video, HeartHandshake, MapPin };

export const Hero = () => {
  return (
    <section
      className="relative overflow-hidden hero-wash pt-36 pb-24 md:pt-44 md:pb-32"
      data-testid="hero-section"
    >
      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* LEFT — Hero text (typography unchanged per user request) */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-3 mb-9"
            >
              <span className="w-10 h-px bg-brand-goldDark" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-medium text-brand-goldDark">
                Neurologist · Psychologist
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="heading-serif text-5xl sm:text-6xl lg:text-[5.25rem] font-medium tracking-tight text-brand-navyDark leading-[1.02]"
            >
              Advanced{" "}
              <span className="relative inline-block">
                <span className="text-brand-tealDark italic">Neurology</span>
                <svg
                  className="absolute -bottom-2 left-0 w-full"
                  viewBox="0 0 200 8"
                  preserveAspectRatio="none"
                  height="8"
                >
                  <path
                    d="M2 6 Q 100 0 198 6"
                    stroke="#7A9B93"
                    strokeWidth="1.5"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>{" "}
              & Mental Wellness Care
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-8 text-lg md:text-xl text-brand-body leading-[1.7] max-w-xl"
            >
              Expert neurological and psychological consultation with{" "}
              <span className="text-brand-navyDark">compassionate, patient-focused</span>{" "}
              treatment — at the clinic and online.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link to="/contact#book" className="btn-primary" data-testid="hero-book-btn">
                <Calendar className="w-3.5 h-3.5" />
                Book Appointment
              </Link>
              <a
                href={CLINIC.whatsapp}
                target="_blank"
                rel="noreferrer"
                className="btn-secondary"
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                WhatsApp
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-5 max-w-2xl"
            >
              {TRUST_BADGES.map((b, i) => {
                const Icon = ICONS[b.icon];
                return (
                  <div
                    key={i}
                    className="flex items-start gap-3 border-l border-brand-navyDark/15 pl-4"
                    data-testid={`trust-badge-${i}`}
                  >
                    <Icon
                      className="w-4 h-4 text-brand-tealDark mt-1 shrink-0"
                      strokeWidth={1.5}
                    />
                    <span className="text-[13px] font-medium text-brand-navyDark leading-snug">
                      {b.text}
                    </span>
                  </div>
                );
              })}
            </motion.div>
          </div>

          {/* RIGHT — Real doctor cutout from uploaded poster (CSS-cropped) */}
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="relative"
            >
              {/* Soft warm wash behind */}
              <div className="absolute -right-8 top-8 -bottom-8 left-8 bg-brand-tealMist/70 -z-10" />
              <div className="absolute -left-6 -top-6 w-32 h-32 border border-brand-goldDark/40 -z-10" />

              <div
                className="relative aspect-[3/4] overflow-hidden bg-[#FAF9F7]"
                data-testid="hero-image"
              >
                <img
                  src={HERO_POSTER}
                  alt="Dr. Avril Dhar — Neurologist & Psychologist"
                  loading="eager"
                  decoding="async"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "8% center", transform: "scale(1.18)" }}
                />
              </div>
              <div className="absolute inset-0 ring-1 ring-brand-navyDark/10 pointer-events-none" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
